import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components/macro';
import { CategoryBreadCrumb } from '../components/pages/category/BreadCrumb';
import { DesktopProductCarousel } from '../components/pages/product/DesktopProductCarousel';
import { MobileProductCarousel } from '../components/pages/product/MobileProductCarousel';
import { ProductInfos } from '../components/pages/product/ProductInfos';
import { ProductSpecs } from '../components/pages/product/ProductSpecs';
import { WImage } from '../components/ui/image/Image';
import { PriceImage } from '../components/ui/image/PriceImage';
import useProduct from '../hooks/api/queries/useProduct';
import { ImageType } from '../hooks/api/queries/useProducts';
import useVariantsByProductId from '../hooks/api/queries/useVariantsByProductId';
import { COLORS } from '../styles/colors';
import { breakpoint } from '../styles/constants';
import { IMAGE_RATIOS } from '../styles/imageRatios';
import { NotFound } from './404';
import { PageWrapper } from './Category';

interface ProductState {
  categorySlug?: string;
}

export const Product = () => {
  const { productSlug } = useParams();
  const [imageSelected, setImageSelected] = useState<number>(0);
  const location = useLocation();
  const filter = {
    productSlug,
  };

  const state = location.state as ProductState;
  const { categorySlug } = state ?? { categorySlug: undefined };
  const { data, isLoading } = useProduct(filter);
  const { data: variants } = useVariantsByProductId(filter);

  const images = (data?.defaultImages ?? [])
    .concat(data?.localizedImages ?? [])
    .concat(
      variants
        ?.filter((v) => v.localizedImage || v.defaultImage)
        ?.map((v) => (v.localizedImage ?? v.defaultImage) as ImageType) ?? [],
    )
    .filter((i) => i.path);

  const uniqImages = [
    ...images
      ?.reduce((a, c) => {
        a.set(c.hash ?? '', c);
        return a;
      }, new Map())
      .values(),
  ];

  const navigate = useNavigate();

  if (!data || !variants)
    if (!isLoading)
      return (
        <>
          {setTimeout(
            () => (
              <NotFound />
            ),
            500,
          )}
          ;
        </>
      );
    else return null;

  return (
    <PageWrapper>
      {categorySlug && (
        <MarginBottom>
          <CategoryBreadCrumb categorySlug={categorySlug} lastClickable />
        </MarginBottom>
      )}
      <DivTop>
        <LeftDiv>
          <BigImageDiv>
            <PriceImage price={data.priceOverwrite} position={'top'} />
            <WImage
              image={
                uniqImages?.[
                  imageSelected >= uniqImages.length || imageSelected < 0
                    ? 0
                    : imageSelected
                ]
              }
              background
            />
            <BackIcon icon={faChevronLeft} onClick={() => navigate(-1)} />
          </BigImageDiv>
          <DesktopProductCarousel
            /* Filtering uniq images ids */
            images={uniqImages}
            setImageSelected={setImageSelected}
            imageSelected={imageSelected}
          />
        </LeftDiv>
        <MobileProductCarousel
          /* Filtering uniq images ids */
          images={uniqImages}
          imageSelected={0}
          setImageSelected={setImageSelected}
          priceOverwrite={data.priceOverwrite}
        />

        <ProductInfos
          images={uniqImages}
          product={data}
          variants={variants}
          setImageSelected={setImageSelected}
          imageSelected={imageSelected}
        />
      </DivTop>

      <ProductSpecs product={data} />
    </PageWrapper>
  );
};

const DivTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-bottom: 1.25rem;

  @media (min-width: ${breakpoint}) {
    flex-direction: row;
    margin-bottom: 3.75rem;
  }
`;

const LeftDiv = styled.div`
  display: none;

  @media (min-width: ${breakpoint}) {
    display: block;
    position: relative;
    width: 55%;
    margin-inline: unset;
    transform: none;
  }
`;

const BigImageDiv = styled.div`
  margin-bottom: 1.25rem;
  img {
    object-fit: cover;
    aspect-ratio: ${IMAGE_RATIOS.PRODUCT_PAGE};
  }
`;

const BackIcon = styled(FontAwesomeIcon)`
  font-weight: 900;
  font-size: 0.875rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.white};

  position: absolute;
  left: 0.75rem;
  top: 1.25rem;
  z-index: 2;

  @media (min-width: ${breakpoint}) {
    top: 0.875rem;
    left: -1.25rem;
    color: ${({ theme }) => theme.colors.white};
  }

  &:hover {
    color: ${COLORS.red};
  }
`;

const MarginBottom = styled.div`
  margin-bottom: 0.4rem;
`;
